"use client"

import { useEffect, useRef } from "react"
import type React from "react"
import type { Dispatch, RefObject, SetStateAction } from "react"
import type { BooruPost } from "@/lib/booru-api"
import { useGlobalPlaybackStore } from "@/lib/global-playback-store"
import { useVRStore } from "@/lib/vr-store"

// Moved out of components/booru-detail-modal.tsx: the same effects, in the same order, with the same dependencies, and the
// same handlers. The video state itself stays in the modal (its reset effects, keyboard shortcuts and controls read it);
// this hook only gets it passed in. Two handlers with no caller anywhere (handleSpeedChange, handleProgressChange) were dropped.
const BOORU_SCRIPT_PLAY_OPTIONS = { skipHSSP: false } as const

export interface VideoPlaybackInput {
  /** The mounted <video> (see use-mounted-video.ts); only used to re-attach the script sync when it appears. */
  videoEl: HTMLVideoElement | null
  abLoopEnabled: boolean
  activeVideoUrl: string | null
  animationFrameRef: RefObject<number | undefined>
  canPlayListenerRef: RefObject<(() => void) | null>
  duration: number
  getCurrentTime: () => number
  globalPause: () => void
  globalPlay: (opts?: { skipHSSP?: boolean }) => Promise<void>
  isLooping: boolean
  isMediaVideo: boolean
  isMuted: boolean
  isPlaying: boolean
  isVRMode: boolean
  isVideo: boolean
  lastTimeUpdateRef: RefObject<number>
  loopPointA: number | null
  loopPointB: number | null
  pauseBackgroundPlayback: () => void
  pendingPlayRef: RefObject<boolean>
  playbackSpeed: number
  post: BooruPost | null
  setCurrentTime: (t: number) => void
  setIsMuted: Dispatch<SetStateAction<boolean>>
  setIsPlaying: Dispatch<SetStateAction<boolean>>
  setIsVideoLoading: Dispatch<SetStateAction<boolean>>
  setPlaybackSpeed: Dispatch<SetStateAction<number>>
  setVideoError: Dispatch<SetStateAction<string | null>>
  showScriptPlayer: boolean
  userChangedMuteRef: RefObject<boolean>
  videoAutoMute: boolean
  videoPitchCorrection: boolean
  videoRef: RefObject<HTMLVideoElement | null>
  volume: number
}

export function useVideoPlayback({
  videoEl,
  abLoopEnabled,
  activeVideoUrl,
  animationFrameRef,
  canPlayListenerRef,
  duration,
  getCurrentTime,
  globalPause,
  globalPlay,
  isLooping,
  isMediaVideo,
  isMuted,
  isPlaying,
  isVRMode,
  isVideo,
  lastTimeUpdateRef,
  loopPointA,
  loopPointB,
  pauseBackgroundPlayback,
  pendingPlayRef,
  playbackSpeed,
  post,
  setCurrentTime,
  setIsMuted,
  setIsPlaying,
  setIsVideoLoading,
  setPlaybackSpeed,
  setVideoError,
  showScriptPlayer,
  userChangedMuteRef,
  videoAutoMute,
  videoPitchCorrection,
  videoRef,
  volume,
}: VideoPlaybackInput) {
  // Helper function to check if video is ready to play
  const checkVideoReady = (video: HTMLVideoElement): boolean => {
    const isReady = video.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA
    console.log("[video-play] Check video ready", {
      readyState: video.readyState,
      readyStateText: ['HAVE_NOTHING', 'HAVE_METADATA', 'HAVE_CURRENT_DATA', 'HAVE_FUTURE_DATA', 'HAVE_ENOUGH_DATA'][video.readyState],
      isReady,
      threshold: HTMLMediaElement.HAVE_FUTURE_DATA,
    })
    return isReady
  }

  // Helper function to attempt video playback with proper error handling
  const attemptPlay = async (video: HTMLVideoElement) => {
    console.log("[video-play] Attempting to play video", {
      readyState: video.readyState,
      readyStateText: ['HAVE_NOTHING', 'HAVE_METADATA', 'HAVE_CURRENT_DATA', 'HAVE_FUTURE_DATA', 'HAVE_ENOUGH_DATA'][video.readyState],
      paused: video.paused,
      currentTime: video.currentTime,
      duration: video.duration,
      networkState: video.networkState,
    })
    
    try {
      setIsVideoLoading(true)
      const playPromise = video.play()
      console.log("[video-play] Play promise created", { promiseExists: !!playPromise })
      
      await playPromise
      
      console.log("[video-play] Play succeeded", {
        paused: video.paused,
        currentTime: video.currentTime,
      })
      
      setIsVideoLoading(false)
      setVideoError(null)
      pendingPlayRef.current = false
    } catch (error) {
      console.error("[video-play] Play failed with error", {
        error,
        errorName: error instanceof DOMException ? error.name : 'Unknown',
        errorMessage: error instanceof Error ? error.message : String(error),
        readyState: video.readyState,
        paused: video.paused,
      })
      
      setIsVideoLoading(false)
      if (error instanceof DOMException) {
        if (error.name === "NotAllowedError") {
          setVideoError("Click to play - autoplay blocked")
          setIsPlaying(false)
        } else if (error.name === "NotSupportedError") {
          setVideoError("Video format not supported")
          setIsPlaying(false)
        } else if (error.name !== "AbortError") {
          console.error("[video-play] Play failed:", error)
          setVideoError("Playback failed")
          setIsPlaying(false)
        }
      }
      pendingPlayRef.current = false
    }
  }

  // Handler for canplay event
  const handleCanPlay = () => {
    console.log("[video-play] Canplay event fired", {
      pendingPlay: pendingPlayRef.current,
      videoExists: !!videoRef.current,
      readyState: videoRef.current?.readyState,
    })
    
    if (pendingPlayRef.current && videoRef.current) {
      attemptPlay(videoRef.current)
    }
  }

  useEffect(() => {
    const video = videoRef.current
    if (!video) return

    video.volume = volume
    video.muted = isMuted
    video.playbackRate = playbackSpeed
    video.preservesPitch = videoPitchCorrection

    if (isPlaying) {
      const updateFrame = () => {
        const v = videoRef.current
        if (!v) return

        // This loop runs on requestAnimationFrame (60-90Hz, matching the display, not the
        // video's own ~24-60fps), and setCurrentTime is React state — every call here was
        // a full re-render of this ~5800-line modal at display refresh rate. That's on the
        // same main thread as an XR render loop when this component's VROverlay is active,
        // and was reported as "playhead skipping/jittering" and choppy VR playback whenever
        // this modal was involved. A seek bar doesn't need sub-frame precision — ~8 updates
        // a second reads as smooth motion and cuts the re-render rate by an order of
        // magnitude. (The A-B loop check below reads video.currentTime directly on its own
        // interval, not this state, so it's unaffected.)
        // While the VR player is open this whole modal is hidden under it, so skip the
        // update entirely — profiled on the Quest: it was still committing the full app tree
        // 8x/sec in immersive, stealing main-thread time from the XR render loop. The
        // isVRMode effect below re-syncs the time once on exit.
        const now = performance.now()
        if (!useVRStore.getState().isVRMode && now - lastTimeUpdateRef.current >= 120) {
          lastTimeUpdateRef.current = now
          setCurrentTime(v.currentTime)
        }

        if (v.ended) {
          const hasDuration = Number.isFinite(v.duration) && v.duration > 0
          if (isLooping && hasDuration) {
            v.currentTime = 0
            const playPromise = v.play()
            if (playPromise !== undefined) {
              playPromise.catch((error) => {
                if (error instanceof DOMException && error.name === "AbortError") return
                console.error("[v0] Detail modal loop play failed:", error)
              })
            }
          } else {
            setIsPlaying(false)
            return
          }
        }
        animationFrameRef.current = requestAnimationFrame(updateFrame)
      }

      // Clean up any existing canplay listener
      if (canPlayListenerRef.current) {
        console.log("[video-play] Removing existing canplay listener")
        video.removeEventListener("canplay", canPlayListenerRef.current)
        canPlayListenerRef.current = null
      }

      console.log("[video-play] Forward playback mode, checking if ready")
      // Check if video is ready to play
      if (checkVideoReady(video)) {
        // Video is ready, play immediately
        console.log("[video-play] Video is ready, playing immediately")
        attemptPlay(video)
      } else {
        // Video not ready, wait for canplay event
        console.log("[video-play] Video not ready, waiting for canplay event")
        pendingPlayRef.current = true
        setIsVideoLoading(true)

        const listener = () => handleCanPlay()
        canPlayListenerRef.current = listener
        video.addEventListener("canplay", listener, { once: true })
        console.log("[video-play] Canplay listener attached")
      }

      animationFrameRef.current = requestAnimationFrame(updateFrame)

      return () => {
        if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current)
        if (canPlayListenerRef.current && videoRef.current) {
          videoRef.current.removeEventListener("canplay", canPlayListenerRef.current)
          canPlayListenerRef.current = null
        }
      }
    } else {
      // User paused - clear pending play
      console.log("[video-play] Pausing video", {
        pendingPlay: pendingPlayRef.current,
        hasListener: !!canPlayListenerRef.current,
      })
      
      pendingPlayRef.current = false
      video.pause()
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current)
      }
    }
  }, [isPlaying, playbackSpeed, isLooping, volume, isMuted, videoPitchCorrection])

  // Playhead updates are skipped while the VR player covers this modal; catch the time up
  // once on the way back out so the modal's own seek bar isn't left stale.
  useEffect(() => {
    if (!isVRMode && videoRef.current) setCurrentTime(videoRef.current.currentTime)
  }, [isVRMode])

  // A-B Loop effect
  useEffect(() => {
    if (!abLoopEnabled || !loopPointA || !loopPointB || !isPlaying) {
      return
    }

    const video = videoRef.current
    if (!video) return

    const loopCheckInterval = setInterval(() => {
      if (video.currentTime >= loopPointB) {
        video.currentTime = loopPointA
        setCurrentTime(loopPointA)
      }
    }, 100) // Check every 100ms

    return () => {
      clearInterval(loopCheckInterval)
    }
  }, [abLoopEnabled, loopPointA, loopPointB, isPlaying])

  useEffect(() => {
    if (videoRef.current && isVideo) {
      if (videoRef.current.currentTime === 0 && !userChangedMuteRef.current) {
        setIsMuted(videoAutoMute)
      }
    }
  }, [post?.id, videoAutoMute, isVideo])

  useEffect(() => {
    if (showScriptPlayer) {
      pauseBackgroundPlayback()
    }
  }, [showScriptPlayer, pauseBackgroundPlayback])

  // Shared suppress ref — ContentScriptsPanel sets these before calling video.play/pause
  // so the sync effect below doesn't double-trigger the script engine.
  const videoSyncSuppressRef = useRef({ play: false, pause: false })

  // Video ↔ script sync — reattaches whenever the video element changes (activeVideoUrl change = new key = remount)
  useEffect(() => {
    const video = videoRef.current
    if (!video || !isMediaVideo) return

    // T6 (docs/VR_TASKS.md): the modal's own `isPlaying` state (drives the RAF loop above)
    // was only ever set from this component's own button clicks — a play/pause triggered
    // from inside VR (the immersive bar, the controller's A/X button) called video.play()/
    // pause() directly and left this state stale, which is why VR-triggered pauses looked
    // like they'd done nothing back in the flat modal.
    const onPlay = () => {
      setIsPlaying(true)
      if (videoSyncSuppressRef.current.play) { videoSyncSuppressRef.current.play = false; return }
      const { currentScript, isPlaying } = useGlobalPlaybackStore.getState()
      if (currentScript && !isPlaying) globalPlay(BOORU_SCRIPT_PLAY_OPTIONS).catch(() => {})
    }
    const onPause = () => {
      setIsPlaying(false)
      if (videoSyncSuppressRef.current.pause) { videoSyncSuppressRef.current.pause = false; return }
      const { currentScript, isPlaying } = useGlobalPlaybackStore.getState()
      if (currentScript && isPlaying) globalPause()
    }
    const onTimeUpdate = () => {
      const { currentScript, isPlaying, syncVideoTime } = useGlobalPlaybackStore.getState()
      if (currentScript && isPlaying) syncVideoTime(video.currentTime)
    }
    const onSeeked = () => {
      const { currentScript, setCurrentTime } = useGlobalPlaybackStore.getState()
      if (currentScript) setCurrentTime(video.currentTime)
    }
    const onEnded = () => {
      const { currentScript, stop } = useGlobalPlaybackStore.getState()
      if (currentScript) stop()
    }

    video.addEventListener("play", onPlay)
    video.addEventListener("pause", onPause)
    video.addEventListener("timeupdate", onTimeUpdate)
    video.addEventListener("seeked", onSeeked)
    video.addEventListener("ended", onEnded)
    return () => {
      video.removeEventListener("play", onPlay)
      video.removeEventListener("pause", onPause)
      video.removeEventListener("timeupdate", onTimeUpdate)
      video.removeEventListener("seeked", onSeeked)
      video.removeEventListener("ended", onEnded)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMediaVideo, activeVideoUrl, videoEl])

  const handlePlayPause = () => {
    const video = videoRef.current
    console.log("[video-play] Play button clicked", {
      currentIsPlaying: isPlaying,
      newIsPlaying: !isPlaying,
      videoExists: !!video,
      readyState: video?.readyState,
      readyStateText: video ? ['HAVE_NOTHING', 'HAVE_METADATA', 'HAVE_CURRENT_DATA', 'HAVE_FUTURE_DATA', 'HAVE_ENOUGH_DATA'][video.readyState] : 'N/A',
      networkState: video?.networkState,
      paused: video?.paused,
      currentTime: video?.currentTime,
      duration: video?.duration,
      src: video?.src,
      pendingPlay: pendingPlayRef.current,
    })
    setIsPlaying(!isPlaying)
  }

  // Frame navigation handlers (assuming 30fps)
  const handleFrameForward = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (!videoRef.current || !duration) return
    const frameTime = 1 / 30 // ~33ms per frame at 30fps
    const newTime = Math.min(getCurrentTime() + frameTime, duration)
    videoRef.current.currentTime = newTime
    setCurrentTime(newTime)
  }

  const handleFrameBackward = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (!videoRef.current || !duration) return
    const frameTime = 1 / 30 // ~33ms per frame at 30fps
    const newTime = Math.max(getCurrentTime() - frameTime, 0)
    videoRef.current.currentTime = newTime
    setCurrentTime(newTime)
  }

  return { videoSyncSuppressRef, handlePlayPause, handleFrameForward, handleFrameBackward }
}
